import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend } from "recharts";

function UserStats({
  username,
  userSubmissions,
  userSubmissionsLoading,
  userSubmissionsError,
  problemsWithStats,
  onUsernameChange,
  toggleDarkMode,
  isDarkMode,
  getRatingColor,
  getDifficultyText,
}) {
  const navigate = useNavigate();
  const [showUsernameChange, setShowUsernameChange] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [isChangingUsername, setIsChangingUsername] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [userRatingHistory, setUserRatingHistory] = useState([]);
  const [userInfo, setUserInfo] = useState(null);

  // Fetch user rating history and info
  useEffect(() => {
    const fetchUserData = async () => {
      if (!username) return;
      try {
        // Fetch user rating history
        const ratingResponse = await fetch(`https://codeforces.com/api/user.rating?handle=${username}`);
        const ratingData = await ratingResponse.json();

        if (ratingData.status === "OK") {
          setUserRatingHistory(ratingData.result);
        }

        // Fetch user info
        const infoResponse = await fetch(`https://codeforces.com/api/user.info?handles=${username}`);
        const infoData = await infoResponse.json();

        if (infoData.status === "OK" && infoData.result.length > 0) {
          setUserInfo(infoData.result[0]);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, [username]);

  // Calculate user statistics
  const userStats = useMemo(() => {
    if (!userSubmissions || !userSubmissions.length || !problemsWithStats) return null;

    const solvedProblems = new Set();
    const attemptedProblems = new Set();
    const submissionsByProblem = new Map();
    const ratingDistribution = {};
    const tagDistribution = {};
    const languageDistribution = {};
    const verdictDistribution = {};
    const submissionTrends = {};
    const timeDistribution = { morning: 0, afternoon: 0, evening: 0, night: 0 };
    const contestSubmissions = {};
    let totalSubmissions = 0;
    let successfulSubmissions = 0;

    userSubmissions.forEach((submission) => {
      const problemKey = `${submission.problem.contestId}-${submission.problem.index}`;
      attemptedProblems.add(problemKey);

      // Track submissions per problem
      if (!submissionsByProblem.has(problemKey)) {
        submissionsByProblem.set(problemKey, []);
      }
      submissionsByProblem.get(problemKey).push(submission);

      // Track verdicts
      const verdict = submission.verdict || "UNKNOWN";
      verdictDistribution[verdict] = (verdictDistribution[verdict] || 0) + 1;

      // Track languages
      const language = submission.programmingLanguage;
      languageDistribution[language] = (languageDistribution[language] || 0) + 1;

      // Track submission trends by month
      const submissionDate = new Date(submission.creationTimeSeconds * 1000);
      const monthKey = `${submissionDate.getFullYear()}-${String(submissionDate.getMonth() + 1).padStart(2, "0")}`;
      if (!submissionTrends[monthKey]) {
        submissionTrends[monthKey] = { month: monthKey, submissions: 0, accepted: 0 };
      }
      submissionTrends[monthKey].submissions++;
      if (submission.verdict === "OK") {
        submissionTrends[monthKey].accepted++;
      }

      // Track time distribution
      const hour = submissionDate.getHours();
      if (hour >= 6 && hour < 12) timeDistribution.morning++;
      else if (hour >= 12 && hour < 18) timeDistribution.afternoon++;
      else if (hour >= 18 && hour < 22) timeDistribution.evening++;
      else timeDistribution.night++;

      // Track contest submissions
      if (submission.contestId) {
        contestSubmissions[submission.contestId] = (contestSubmissions[submission.contestId] || 0) + 1;
      }

      totalSubmissions++;

      if (submission.verdict === "OK") {
        solvedProblems.add(problemKey);
        successfulSubmissions++;

        // Track rating distribution for solved problems
        if (submission.problem.rating) {
          const rating = submission.problem.rating;
          ratingDistribution[rating] = (ratingDistribution[rating] || 0) + 1;
        }

        // Track tag distribution for solved problems
        submission.problem.tags.forEach((tag) => {
          tagDistribution[tag] = (tagDistribution[tag] || 0) + 1;
        });
      }
    });

    // Calculate average attempts per problem
    const avgAttemptsPerProblem = totalSubmissions / attemptedProblems.size;

    // Get most attempted unsolved problems
    const unsolvedProblems = Array.from(attemptedProblems)
      .filter((problemKey) => !solvedProblems.has(problemKey))
      .map((problemKey) => {
        const submissions = submissionsByProblem.get(problemKey);
        const problem = submissions[0].problem;
        return {
          contestId: problem.contestId,
          index: problem.index,
          name: problem.name,
          rating: problem.rating,
          tags: problem.tags,
          attempts: submissions.length,
          lastAttempt: Math.max(...submissions.map((s) => s.creationTimeSeconds)),
        };
      })
      .sort((a, b) => b.attempts - a.attempts)
      .slice(0, 10);

    // Get recent solved problems
    const recentSolved = Array.from(solvedProblems)
      .map((problemKey) => {
        const submissions = submissionsByProblem.get(problemKey);
        const successfulSubmission = submissions.find((s) => s.verdict === "OK");
        const problem = successfulSubmission.problem;
        return {
          contestId: problem.contestId,
          index: problem.index,
          name: problem.name,
          rating: problem.rating,
          tags: problem.tags,
          solvedAt: successfulSubmission.creationTimeSeconds,
        };
      })
      .sort((a, b) => b.solvedAt - a.solvedAt)
      .slice(0, 10);

    // Process submission trends
    const submissionTrendsArray = Object.values(submissionTrends).sort((a, b) => a.month.localeCompare(b.month));

    // Shuffle array function for randomness
    const shuffleArray = (array) => {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };

    // Generate recommended problems
    const generateRecommendations = () => {
      if (!problemsWithStats || problemsWithStats.length === 0) return [];

      // Get user's comfort rating range
      const solvedRatings = Object.keys(ratingDistribution).map((r) => parseInt(r));
      const avgRating = solvedRatings.length > 0 ? solvedRatings.reduce((sum, rating) => sum + rating, 0) / solvedRatings.length : 1200;

      // Get weakest tags (tags with fewer solved problems)
      const tagCounts = Object.entries(tagDistribution);
      const weakTags =
        tagCounts.length > 0
          ? tagCounts
              .sort(([, a], [, b]) => a - b)
              .slice(0, 5)
              .map(([tag]) => tag)
          : [];

      // Get strongest tags for variety
      const strongTags =
        tagCounts.length > 0
          ? tagCounts
              .sort(([, a], [, b]) => b - a)
              .slice(0, 3)
              .map(([tag]) => tag)
          : [];

      // Filter unsolved problems
      const unsolvedSet = new Set();
      problemsWithStats.forEach((problem) => {
        const key = `${problem.contestId}-${problem.index}`;
        if (!solvedProblems.has(key)) {
          unsolvedSet.add(problem);
        }
      });

      // Recommendation 1: Practice weak tags at comfortable rating
      const weakTagCandidates = Array.from(unsolvedSet).filter(
        (problem) => problem.rating && Math.abs(problem.rating - avgRating) <= 250 && problem.tags.some((tag) => weakTags.includes(tag))
      );
      const weakTagProblems = shuffleArray(weakTagCandidates).slice(0, 6);

      // Recommendation 2: Step up difficulty in strong areas (more varied range)
      const stepUpCandidates = Array.from(unsolvedSet).filter(
        (problem) => problem.rating && problem.rating > avgRating + 50 && problem.rating <= avgRating + 400 && problem.tags.some((tag) => strongTags.includes(tag))
      );
      const stepUpProblems = shuffleArray(stepUpCandidates).slice(0, 5);

      // Recommendation 3: Easy confidence boost (much easier problems for confidence)
      const easyCandidates = Array.from(unsolvedSet).filter(
        (problem) => problem.rating && problem.rating >= avgRating - 500 && problem.rating < avgRating - 100 && !problem.tags.some((tag) => weakTags.includes(tag))
      );
      const easyWins = shuffleArray(easyCandidates).slice(0, 7);

      const allRecommendations = [
        ...weakTagProblems.map((p) => ({ ...p, reason: "Practice weak areas", type: "weakness" })),
        ...stepUpProblems.map((p) => ({ ...p, reason: "Challenge in strong areas", type: "challenge" })),
        ...easyWins.map((p) => ({ ...p, reason: "Easy confidence boost", type: "easy" })),
      ];

      // Final shuffle to mix different types together and ensure variety on each refresh
      return shuffleArray(allRecommendations).slice(0, 16);
    };

    const recommendedProblems = generateRecommendations();

    return {
      totalProblems: problemsWithStats.length,
      solvedProblems: solvedProblems.size,
      attemptedProblems: attemptedProblems.size,
      unsolvedProblemsCount: problemsWithStats.length - solvedProblems.size,
      totalSubmissions,
      successfulSubmissions,
      successRate: ((successfulSubmissions / totalSubmissions) * 100).toFixed(1),
      avgAttemptsPerProblem: avgAttemptsPerProblem.toFixed(1),
      ratingDistribution,
      tagDistribution,
      languageDistribution,
      verdictDistribution,
      unsolvedProblems,
      recentSolved,
      submissionTrends: submissionTrendsArray,
      timeDistribution,
      contestSubmissions,
      recommendedProblems,
    };
  }, [userSubmissions, problemsWithStats]);

  const handleUsernameChange = async (e) => {
    e.preventDefault();
    if (!newUsername.trim()) {
      setUsernameError("Please enter a username");
      return;
    }

    setIsChangingUsername(true);
    setUsernameError("");

    try {
      const response = await fetch(`https://codeforces.com/api/user.status?handle=${newUsername.trim()}&count=1`);
      const data = await response.json();

      if (data.status === "OK") {
        onUsernameChange(newUsername.trim());
        setShowUsernameChange(false);
        setNewUsername("");
      } else {
        setUsernameError(data.comment || "Username not found. Please check the spelling.");
      }
    } catch (err) {
      setUsernameError("Failed to verify username. Please try again.");
    } finally {
      setIsChangingUsername(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  // Chart color schemes
  const chartColors = {
    primary: isDarkMode ? "#3B82F6" : "#2563EB",
    secondary: isDarkMode ? "#8B5CF6" : "#7C3AED",
    success: isDarkMode ? "#10B981" : "#059669",
    warning: isDarkMode ? "#F59E0B" : "#D97706",
    error: isDarkMode ? "#EF4444" : "#DC2626",
    info: isDarkMode ? "#06B6D4" : "#0891B2",
  };

  // Prepare rating history data for chart
  const ratingChartData = userRatingHistory.map((contest) => ({
    contest: contest.contestName.length > 20 ? contest.contestName.substring(0, 20) + "..." : contest.contestName,
    rating: contest.newRating,
    change: contest.newRating - contest.oldRating,
    date: new Date(contest.ratingUpdateTimeSeconds * 1000).toLocaleDateString(),
  }));

  // Prepare tag analysis data for charts
  const tagChartData = userStats
    ? Object.entries(userStats.tagDistribution)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 12)
        .map(([tag, count]) => ({
          name: tag.length > 15 ? tag.substring(0, 15) + "..." : tag,
          fullName: tag,
          value: count,
          percentage: ((count / userStats.solvedProblems) * 100).toFixed(1),
        }))
    : [];

  // Prepare rating range analysis
  const ratingRanges = {
    "800-1199": { name: "Beginner", color: "#6B7280", count: 0, range: [800, 1199] },
    "1200-1399": { name: "Pupil", color: "#10B981", count: 0, range: [1200, 1399] },
    "1400-1599": { name: "Specialist", color: "#06B6D4", count: 0, range: [1400, 1599] },
    "1600-1899": { name: "Expert", color: "#3B82F6", count: 0, range: [1600, 1899] },
    "1900-2099": { name: "Candidate Master", color: "#8B5CF6", count: 0, range: [1900, 2099] },
    "2100-2299": { name: "Master", color: "#F59E0B", count: 0, range: [2100, 2299] },
    "2300+": { name: "International Master+", color: "#EF4444", count: 0, range: [2300, 5000] },
  };

  // Count problems by rating ranges
  if (userStats) {
    Object.entries(userStats.ratingDistribution).forEach(([rating, count]) => {
      const ratingNum = parseInt(rating);
      for (const [, range] of Object.entries(ratingRanges)) {
        if (ratingNum >= range.range[0] && ratingNum <= range.range[1]) {
          range.count += count;
          break;
        }
      }
    });
  }

  const ratingRangeChartData = Object.entries(ratingRanges)
    .filter(([, data]) => data.count > 0)
    .map(([range, data]) => ({
      name: data.name,
      range: range,
      value: data.count,
      color: data.color,
      percentage: userStats ? ((data.count / userStats.solvedProblems) * 100).toFixed(1) : 0,
    }));

  // Tag mastery analysis (tags with >3 problems solved) - commented out as not currently used
  // const tagMasteryData = userStats
  //   ? Object.entries(userStats.tagDistribution)
  //       .filter(([, count]) => count >= 3)
  //       .sort(([, a], [, b]) => b - a)
  //       .slice(0, 10)
  //       .map(([tag, count]) => ({
  //         name: tag,
  //         solved: count,
  //         mastery: count >= 10 ? "Expert" : count >= 5 ? "Intermediate" : "Beginner",
  //         masteryColor: count >= 10 ? chartColors.success : count >= 5 ? chartColors.warning : chartColors.info,
  //       }))
  //   : [];

  if (userSubmissionsLoading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-all duration-500 ${
          isDarkMode ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" : "bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50"
        }`}
      >
        <div className="text-center">
          <div className={`animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-6 ${isDarkMode ? "border-blue-400" : "border-blue-600"}`}></div>
          <p className={`text-lg font-medium ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>Loading user statistics...</p>
        </div>
      </div>
    );
  }

  if (userSubmissionsError) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-all duration-500 ${
          isDarkMode ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" : "bg-gradient-to-br from-red-50 to-pink-100"
        }`}
      >
        <div className="text-center">
          <div className={`p-4 rounded-full mb-6 ${isDarkMode ? "bg-red-500/20" : "bg-red-100"}`}>
            <i className="fas fa-exclamation-triangle text-red-500 text-3xl"></i>
          </div>
          <p className={`text-lg font-medium mb-4 ${isDarkMode ? "text-slate-300" : "text-red-600"}`}>{userSubmissionsError}</p>
          <button
            onClick={() => navigate("/")}
            className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 hover:scale-105 ${
              isDarkMode
                ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                : "bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white"
            }`}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen font-['Inter'] transition-all duration-500 ${
        isDarkMode ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100" : "bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50"
      }`}
    >
      {/* Header */}
      <header className={`backdrop-blur-xl border-b transition-all duration-500 ${isDarkMode ? "bg-slate-900/80 border-slate-700/50" : "bg-white/80 border-slate-200/50"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/")}
                className={`p-3 rounded-2xl transition-all duration-300 hover:scale-105 ${
                  isDarkMode ? "bg-slate-800/50 hover:bg-slate-700/50 text-slate-300" : "bg-slate-100/80 hover:bg-slate-200/80 text-slate-600"
                }`}
                title="Back to Home"
              >
                <i className="fas fa-arrow-left"></i>
              </button>
              <div className={`p-3 rounded-2xl ${isDarkMode ? "bg-gradient-to-br from-blue-500 to-purple-600" : "bg-gradient-to-br from-blue-600 to-indigo-700"}`}>
                <i className="fas fa-chart-bar text-white text-xl"></i>
              </div>
              <div>
                <h1 className={`text-2xl font-bold bg-gradient-to-r ${isDarkMode ? "from-slate-100 to-slate-300" : "from-slate-900 to-slate-700"} bg-clip-text text-transparent`}>
                  User Statistics
                </h1>
                <div className={`text-sm mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                  <div className="flex items-center space-x-4">
                    <span>{username}</span>
                    {userInfo && (
                      <>
                        {userInfo.rating && (
                          <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getRatingColor(userInfo.rating)}`}>
                            {userInfo.rating} ({userInfo.rank})
                          </span>
                        )}
                        {userInfo.maxRating && (
                          <span className={`text-xs ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                            Max: {userInfo.maxRating} ({userInfo.maxRank})
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <div className="mt-1">
                    {userStats ? `${userStats.solvedProblems} problems solved` : "Loading..."}
                    {userInfo && userInfo.contribution !== undefined && (
                      <span className={`ml-2 text-xs ${userInfo.contribution >= 0 ? "text-green-500" : "text-red-500"}`}>
                        Contribution: {userInfo.contribution > 0 ? "+" : ""}
                        {userInfo.contribution}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowUsernameChange(!showUsernameChange)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:scale-105 ${
                  isDarkMode ? "bg-slate-800/50 text-slate-300 hover:bg-slate-700/50" : "bg-slate-100/80 text-slate-600 hover:bg-slate-200/80"
                }`}
                title="Change username"
              >
                <i className="fas fa-user-edit mr-2"></i>
                Change User
              </button>
              <button
                onClick={toggleDarkMode}
                className={`p-3 rounded-xl transition-all duration-300 hover:scale-105 ${
                  isDarkMode ? "bg-slate-800/50 hover:bg-slate-700/50 text-slate-300" : "bg-slate-100/80 hover:bg-slate-200/80 text-slate-600"
                }`}
                title="Toggle dark mode"
              >
                <i className={`fas ${isDarkMode ? "fa-sun" : "fa-moon"}`}></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Username Change Modal */}
      {showUsernameChange && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
          <div
            className={`w-full max-w-md rounded-3xl backdrop-blur-xl p-8 transition-all duration-500 ${
              isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/80 border border-slate-200/50 shadow-xl"
            }`}
          >
            <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Change Username</h3>
            <form onSubmit={handleUsernameChange} className="space-y-4">
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>New Codeforces Username</label>
                <input
                  type="text"
                  placeholder="Enter new username"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 ${
                    isDarkMode ? "bg-slate-800/50 border-slate-600 text-slate-100 placeholder-slate-400" : "bg-white border-slate-200 text-slate-900 placeholder-slate-500"
                  }`}
                  disabled={isChangingUsername}
                />
              </div>
              {usernameError && (
                <div className={`p-3 rounded-xl text-sm ${isDarkMode ? "bg-red-500/10 text-red-300 border border-red-500/20" : "bg-red-50 text-red-600 border border-red-200"}`}>
                  <i className="fas fa-exclamation-circle mr-2"></i>
                  {usernameError}
                </div>
              )}
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowUsernameChange(false)}
                  className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all duration-300 hover:scale-105 ${
                    isDarkMode ? "bg-slate-700 hover:bg-slate-600 text-slate-300" : "bg-slate-200 hover:bg-slate-300 text-slate-700"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isChangingUsername}
                  className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDarkMode
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                      : "bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white"
                  }`}
                >
                  {isChangingUsername ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Changing...
                    </>
                  ) : (
                    "Change"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {userStats ? (
          <div className="space-y-8">
            {/* User Profile Summary */}
            {userInfo && (
              <div
                className={`rounded-3xl backdrop-blur-xl p-8 mb-8 transition-all duration-500 ${
                  isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/80 border border-slate-200/50 shadow-xl"
                }`}
              >
                <div className="flex items-center space-x-6">
                  <div className={`p-4 rounded-3xl ${isDarkMode ? "bg-gradient-to-br from-blue-600/20 to-purple-600/20" : "bg-gradient-to-br from-blue-100 to-purple-100"}`}>
                    <i className="fas fa-user text-3xl text-blue-500"></i>
                  </div>
                  <div className="flex-1">
                    <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                      {userInfo.firstName || userInfo.lastName ? `${userInfo.firstName || ""} ${userInfo.lastName || ""}`.trim() : username}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className={`${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Handle: </span>
                        <span className={`font-medium ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>{userInfo.handle}</span>
                      </div>
                      {userInfo.country && (
                        <div>
                          <span className={`${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Country: </span>
                          <span className={`font-medium ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>{userInfo.country}</span>
                        </div>
                      )}
                      {userInfo.organization && (
                        <div>
                          <span className={`${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Organization: </span>
                          <span className={`font-medium ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>{userInfo.organization}</span>
                        </div>
                      )}
                      {userInfo.registrationTimeSeconds && (
                        <div>
                          <span className={`${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Registered: </span>
                          <span className={`font-medium ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>
                            {new Date(userInfo.registrationTimeSeconds * 1000).getFullYear()}
                          </span>
                        </div>
                      )}
                      {userInfo.friendOfCount !== undefined && (
                        <div>
                          <span className={`${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Friends: </span>
                          <span className={`font-medium ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>{userInfo.friendOfCount}</span>
                        </div>
                      )}
                      {userInfo.lastOnlineTimeSeconds && (
                        <div>
                          <span className={`${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Last seen: </span>
                          <span className={`font-medium ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>{formatDate(userInfo.lastOnlineTimeSeconds)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div
                className={`rounded-3xl backdrop-blur-xl p-6 transition-all duration-500 hover:scale-105 ${
                  isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/80 border border-slate-200/50 shadow-xl"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-2xl ${isDarkMode ? "bg-green-500/20" : "bg-green-100"}`}>
                    <i className="fas fa-check-circle text-green-500 text-xl"></i>
                  </div>
                </div>
                <h3 className={`text-2xl font-bold mb-1 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{userStats?.solvedProblems?.toLocaleString() || "0"}</h3>
                <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Problems Solved</p>
                <p className={`text-xs mt-2 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                  {userStats ? ((userStats.solvedProblems / userStats.totalProblems) * 100).toFixed(1) : 0}% of all problems
                </p>
              </div>

              <div
                className={`rounded-3xl backdrop-blur-xl p-6 transition-all duration-500 hover:scale-105 ${
                  isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/80 border border-slate-200/50 shadow-xl"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-2xl ${isDarkMode ? "bg-blue-500/20" : "bg-blue-100"}`}>
                    <i className="fas fa-code text-blue-500 text-xl"></i>
                  </div>
                </div>
                <h3 className={`text-2xl font-bold mb-1 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{userStats?.totalSubmissions?.toLocaleString() || "0"}</h3>
                <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Total Submissions</p>
                <p className={`text-xs mt-2 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>{userStats?.successRate || 0}% success rate</p>
              </div>

              <div
                className={`rounded-3xl backdrop-blur-xl p-6 transition-all duration-500 hover:scale-105 ${
                  isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/80 border border-slate-200/50 shadow-xl"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-2xl ${isDarkMode ? "bg-purple-500/20" : "bg-purple-100"}`}>
                    <i className="fas fa-play text-purple-500 text-xl"></i>
                  </div>
                </div>
                <h3 className={`text-2xl font-bold mb-1 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{userStats?.attemptedProblems?.toLocaleString() || "0"}</h3>
                <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Problems Attempted</p>
                <p className={`text-xs mt-2 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>{userStats?.avgAttemptsPerProblem || 0} avg attempts per problem</p>
              </div>

              <div
                className={`rounded-3xl backdrop-blur-xl p-6 transition-all duration-500 hover:scale-105 ${
                  isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/80 border border-slate-200/50 shadow-xl"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-2xl ${isDarkMode ? "bg-orange-500/20" : "bg-orange-100"}`}>
                    <i className="fas fa-clock text-orange-500 text-xl"></i>
                  </div>
                </div>
                <h3 className={`text-2xl font-bold mb-1 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{userStats?.unsolvedProblemsCount?.toLocaleString() || "0"}</h3>
                <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Problems Unsolved</p>
                <p className={`text-xs mt-2 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>{userStats?.unsolvedProblemsCount || 0} problems remaining</p>
              </div>
            </div>

            {/* Recommended Problems */}
            {userStats?.recommendedProblems?.length > 0 && (
              <div
                className={`rounded-3xl backdrop-blur-xl p-8 transition-all duration-500 ${
                  isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/80 border border-slate-200/50 shadow-xl"
                }`}
              >
                <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                  <i className="fas fa-lightbulb mr-3 text-yellow-500"></i>
                  Recommended Problems for You
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {userStats.recommendedProblems.map((problem, index) => (
                    <div
                      key={`${problem.contestId}-${problem.index}`}
                      className={`p-6 rounded-2xl transition-all duration-300 hover:scale-105 border-l-4 ${
                        problem.type === "weakness"
                          ? isDarkMode
                            ? "border-l-red-400"
                            : "border-l-red-500"
                          : problem.type === "challenge"
                          ? isDarkMode
                            ? "border-l-blue-400"
                            : "border-l-blue-500"
                          : isDarkMode
                          ? "border-l-green-400"
                          : "border-l-green-500"
                      } ${
                        isDarkMode
                          ? "bg-slate-700/50 hover:bg-slate-600/70 border border-slate-600/50 hover:border-slate-500/50"
                          : "bg-slate-100 hover:bg-slate-200 border border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className={`font-bold text-lg ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}>
                          {problem.contestId}
                          {problem.index}
                        </span>
                        <div className="flex items-center space-x-2">
                          {problem.rating && <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRatingColor(problem.rating)}`}>{problem.rating}</span>}
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              problem.type === "weakness"
                                ? isDarkMode
                                  ? "bg-red-900/30 text-red-300"
                                  : "bg-red-100 text-red-700"
                                : problem.type === "challenge"
                                ? isDarkMode
                                  ? "bg-blue-900/30 text-blue-300"
                                  : "bg-blue-100 text-blue-700"
                                : isDarkMode
                                ? "bg-green-900/30 text-green-300"
                                : "bg-green-100 text-green-700"
                            }`}
                          >
                            <i className={`fas ${problem.type === "weakness" ? "fa-bullseye" : problem.type === "challenge" ? "fa-rocket" : "fa-star"}`}></i>
                          </span>
                        </div>
                      </div>
                      <h3 className={`font-semibold mb-3 text-base ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{problem.name}</h3>
                      <div className={`text-sm mb-3 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                        <strong>Why: </strong>
                        {problem.reason}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {problem.tags.slice(0, 4).map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className={`px-2 py-1 rounded-lg text-xs font-medium ${isDarkMode ? "bg-slate-600/50 text-slate-300" : "bg-slate-200 text-slate-700"}`}
                          >
                            {tag}
                          </span>
                        ))}
                        {problem.tags.length > 4 && (
                          <span className={`px-2 py-1 rounded-lg text-xs font-medium ${isDarkMode ? "bg-slate-600/50 text-slate-400" : "bg-slate-200 text-slate-500"}`}>
                            +{problem.tags.length - 4} more
                          </span>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/problem/${problem.contestId}/${problem.index}`);
                          }}
                          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            isDarkMode
                              ? "bg-blue-600 hover:bg-blue-700 text-white border border-blue-500 hover:border-blue-600"
                              : "bg-blue-500 hover:bg-blue-600 text-white border border-blue-400 hover:border-blue-500"
                          } hover:shadow-lg hover:scale-105`}
                        >
                          <i className="fas fa-info-circle mr-2"></i>
                          Details
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`https://codeforces.com/problemset/problem/${problem.contestId}/${problem.index}`, "_blank");
                          }}
                          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            isDarkMode
                              ? "bg-green-600 hover:bg-green-700 text-white border border-green-500 hover:border-green-600"
                              : "bg-green-500 hover:bg-green-600 text-white border border-green-400 hover:border-green-500"
                          } hover:shadow-lg hover:scale-105`}
                        >
                          <i className="fas fa-code mr-2"></i>
                          Solve
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div className={`p-3 rounded-xl ${isDarkMode ? "bg-red-500/10" : "bg-red-50"}`}>
                    <div className="text-red-500 font-medium text-sm">
                      <i className="fas fa-bullseye mr-2"></i>
                      Practice Weak Areas
                    </div>
                    <div className={`text-xs mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Improve skills in your weaker topics</div>
                  </div>
                  <div className={`p-3 rounded-xl ${isDarkMode ? "bg-blue-500/10" : "bg-blue-50"}`}>
                    <div className="text-blue-500 font-medium text-sm">
                      <i className="fas fa-rocket mr-2"></i>
                      Level Up Challenge
                    </div>
                    <div className={`text-xs mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Push your limits in strong areas</div>
                  </div>
                  <div className={`p-3 rounded-xl ${isDarkMode ? "bg-green-500/10" : "bg-green-50"}`}>
                    <div className="text-green-500 font-medium text-sm">
                      <i className="fas fa-star mr-2"></i>
                      Confidence Boost
                    </div>
                    <div className={`text-xs mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Build momentum with much easier wins</div>
                  </div>
                </div>
              </div>
            )}

            {/* Rating History Chart */}
            {ratingChartData.length > 0 && (
              <div
                className={`rounded-3xl backdrop-blur-xl p-8 transition-all duration-500 ${
                  isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/80 border border-slate-200/50 shadow-xl"
                }`}
              >
                <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                  <i className="fas fa-chart-line mr-3 text-blue-500"></i>
                  Rating History ({ratingChartData.length} contests)
                </h2>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={ratingChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#E5E7EB"} />
                      <XAxis dataKey="date" stroke={isDarkMode ? "#9CA3AF" : "#6B7280"} fontSize={12} />
                      <YAxis stroke={isDarkMode ? "#9CA3AF" : "#6B7280"} fontSize={12} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: isDarkMode ? "#1F2937" : "#FFFFFF",
                          border: `1px solid ${isDarkMode ? "#374151" : "#E5E7EB"}`,
                          borderRadius: "12px",
                          color: isDarkMode ? "#F3F4F6" : "#1F2937",
                        }}
                        formatter={(value, name) => [name === "rating" ? `Rating: ${value}` : `Change: ${value > 0 ? "+" : ""}${value}`, ""]}
                        labelFormatter={(label) => `Date: ${label}`}
                      />
                      <Line
                        type="monotone"
                        dataKey="rating"
                        stroke={chartColors.primary}
                        strokeWidth={3}
                        dot={{ fill: chartColors.primary, strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, stroke: chartColors.primary, strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Tag-Based Analysis Dashboard */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Problem Tags Distribution */}
              {tagChartData.length > 0 && (
                <div
                  className={`rounded-3xl backdrop-blur-xl p-8 transition-all duration-500 relative overflow-hidden ${
                    isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/80 border border-slate-200/50 shadow-xl"
                  }`}
                >
                  {/* Background gradient */}
                  <div
                    className={`absolute inset-0 ${isDarkMode ? "bg-gradient-to-br from-blue-500/5 to-purple-500/5" : "bg-gradient-to-br from-blue-50/50 to-purple-50/50"}`}
                  ></div>

                  <div className="relative">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className={`text-xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                        <i className="fas fa-tags mr-3 text-blue-500"></i>
                        Problem Tags Distribution
                      </h3>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? "bg-blue-500/20 text-blue-300" : "bg-blue-100 text-blue-700"}`}>
                        Top {tagChartData.slice(0, 8).length} Tags
                      </div>
                    </div>

                    <div className="h-80 relative">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <defs>
                            {tagChartData.slice(0, 8).map((entry, index) => (
                              <linearGradient key={`gradient-${index}`} id={`gradient-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor={`hsl(${index * 45 + 20}, 85%, 65%)`} />
                                <stop offset="100%" stopColor={`hsl(${index * 45}, 75%, 45%)`} />
                              </linearGradient>
                            ))}
                          </defs>
                          <Pie
                            data={tagChartData.slice(0, 8)}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={110}
                            paddingAngle={3}
                            dataKey="value"
                            stroke={isDarkMode ? "#1F2937" : "#FFFFFF"}
                            strokeWidth={2}
                          >
                            {tagChartData.slice(0, 8).map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={`url(#gradient-${index})`}
                                style={{
                                  filter: "drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))",
                                  transition: "all 0.3s ease",
                                }}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              backgroundColor: isDarkMode ? "#0F172A" : "#FFFFFF",
                              border: `2px solid ${isDarkMode ? "#334155" : "#E2E8F0"}`,
                              borderRadius: "16px",
                              color: isDarkMode ? "#F1F5F9" : "#0F172A",
                              fontSize: "14px",
                              fontWeight: "500",
                              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                            }}
                            formatter={(value, name, props) => [
                              <span style={{ color: props.color }}>
                                {value} problems ({props.payload.percentage}%)
                              </span>,
                              props.payload.fullName,
                            ]}
                          />
                          <Legend
                            wrapperStyle={{
                              color: isDarkMode ? "#CBD5E1" : "#475569",
                              fontSize: "12px",
                              fontWeight: "500",
                              paddingTop: "16px",
                            }}
                            iconType="circle"
                          />
                        </PieChart>
                      </ResponsiveContainer>

                      {/* Center label */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-center">
                          <div className={`text-2xl font-bold ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                            {tagChartData.slice(0, 8).reduce((sum, item) => sum + item.value, 0)}
                          </div>
                          <div className={`text-xs font-medium ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Total Problems</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Rating Range Distribution */}
              {ratingRangeChartData.length > 0 && (
                <div
                  className={`rounded-3xl backdrop-blur-xl p-8 transition-all duration-500 relative overflow-hidden ${
                    isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/80 border border-slate-200/50 shadow-xl"
                  }`}
                >
                  {/* Background gradient */}
                  <div
                    className={`absolute inset-0 ${isDarkMode ? "bg-gradient-to-br from-purple-500/5 to-pink-500/5" : "bg-gradient-to-br from-purple-50/50 to-pink-50/50"}`}
                  ></div>

                  <div className="relative">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className={`text-xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                        <i className="fas fa-trophy mr-3 text-purple-500"></i>
                        Rating Range Distribution
                      </h3>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? "bg-purple-500/20 text-purple-300" : "bg-purple-100 text-purple-700"}`}>
                        {ratingRangeChartData.reduce((sum, item) => sum + item.value, 0)} Total
                      </div>
                    </div>

                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={ratingRangeChartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }} barCategoryGap="15%">
                          <defs>
                            {ratingRangeChartData.map((entry, index) => (
                              <linearGradient key={`bar-gradient-${index}`} id={`bar-gradient-${index}`} x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor={entry.color} stopOpacity={0.9} />
                                <stop offset="100%" stopColor={entry.color} stopOpacity={0.6} />
                              </linearGradient>
                            ))}
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#E5E7EB"} strokeOpacity={0.3} vertical={false} />
                          <XAxis
                            dataKey="name"
                            stroke={isDarkMode ? "#9CA3AF" : "#6B7280"}
                            fontSize={11}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tick={{
                              fill: isDarkMode ? "#CBD5E1" : "#64748B",
                              fontWeight: "500",
                            }}
                          />
                          <YAxis
                            stroke={isDarkMode ? "#9CA3AF" : "#6B7280"}
                            fontSize={12}
                            tick={{
                              fill: isDarkMode ? "#CBD5E1" : "#64748B",
                              fontWeight: "500",
                            }}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: isDarkMode ? "#0F172A" : "#FFFFFF",
                              border: `2px solid ${isDarkMode ? "#334155" : "#E2E8F0"}`,
                              borderRadius: "16px",
                              color: isDarkMode ? "#F1F5F9" : "#0F172A",
                              fontSize: "14px",
                              fontWeight: "500",
                              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                            }}
                            formatter={(value, name, props) => [
                              <span style={{ color: props.payload.color }}>
                                {value} problems ({props.payload.percentage}%)
                              </span>,
                              `${props.payload.name}`,
                            ]}
                            labelFormatter={(label) => `Rating Range: ${label}`}
                          />
                          <Bar dataKey="value" radius={[8, 8, 0, 0]} stroke={isDarkMode ? "#1F2937" : "#FFFFFF"} strokeWidth={2}>
                            {ratingRangeChartData.map((entry, index) => (
                              <Cell
                                key={`bar-cell-${index}`}
                                fill={`url(#bar-gradient-${index})`}
                                style={{
                                  filter: "drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))",
                                  transition: "all 0.3s ease",
                                }}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Summary stats */}
                    <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                      <div className={`p-3 rounded-xl ${isDarkMode ? "bg-slate-700/30" : "bg-slate-100/50"}`}>
                        <div className={`text-lg font-bold ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>{Math.max(...ratingRangeChartData.map((item) => item.value))}</div>
                        <div className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Highest Count</div>
                      </div>
                      <div className={`p-3 rounded-xl ${isDarkMode ? "bg-slate-700/30" : "bg-slate-100/50"}`}>
                        <div className={`text-lg font-bold ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>{ratingRangeChartData.length}</div>
                        <div className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Rating Levels</div>
                      </div>
                      <div className={`p-3 rounded-xl ${isDarkMode ? "bg-slate-700/30" : "bg-slate-100/50"}`}>
                        <div className={`text-lg font-bold ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                          {Math.round(ratingRangeChartData.reduce((sum, item) => sum + item.value, 0) / ratingRangeChartData.length)}
                        </div>
                        <div className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Average</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-16">
            <div className={`p-6 rounded-full mb-6 inline-block ${isDarkMode ? "bg-slate-800/50" : "bg-slate-100"}`}>
              <i className={`text-4xl ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                <i className="fas fa-user"></i>
              </i>
            </div>
            <p className={`text-xl font-semibold mb-2 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>No submissions found</p>
            <p className={`${isDarkMode ? "text-slate-500" : "text-slate-500"}`}>This user hasn't made any submissions yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserStats;
